import { useMutation, useReactiveVar } from '@apollo/client';
import { MAX_NUM_RETRY_REQUESTS, retry } from '@common/neo.tax';
import { useEffect } from 'react';

import { AuthenticationState } from '@neo.tax/shared/state';

import {
  RECONCILE_USER_STATE,
  ReconcileUserStateMutation,
  ReconcileUserStateMutationVariables,
  GET_USER_FROM_AUTH,
  GetUserFromAuthQueryVariables,
} from '../graphql';
import { useUserFromAuth } from './useUserFromAuth';

export const useUserStateReconciliation = (): void => {
  const { userId } = useUserFromAuth();
  const {
    authenticationServiceState: { authenticationUser },
  } = useReactiveVar(AuthenticationState.authenticationStateVar);

  const [reconcileUserState] = useMutation<ReconcileUserStateMutation, ReconcileUserStateMutationVariables>(
    RECONCILE_USER_STATE,
  );

  useEffect(() => {
    (async (): Promise<void> => {
      // Skip reconciliation for unverified Firebase sessions. The backend
      // guard rejects authenticated calls with EMAIL_NOT_VERIFIED until the
      // user completes verification, so retrying here would loop forever. The
      // user is redirected to /verify-email-sent by useAuthRedirect.
      if (!userId || !authenticationUser?.uid || !authenticationUser.emailVerified) {
        return;
      }
      // TODO: When reconciliation cannot complete, show error screen
      // https://linear.app/neo-tax/issue/NEO-2589/when-reconciliation-cannot-complete-show-error-screen
      await retry(
        () =>
          reconcileUserState({
            variables: { userId },
            refetchQueries: [
              {
                query: GET_USER_FROM_AUTH,
                variables: { firebaseUserId: authenticationUser.uid } satisfies GetUserFromAuthQueryVariables,
              },
            ],
          }),
        {
          errorMessage: 'Failed to reconcile user state',
          numRetries: MAX_NUM_RETRY_REQUESTS,
        },
      );
      AuthenticationState.updateUserReconciled();
    })();
  }, [reconcileUserState, userId, authenticationUser?.uid, authenticationUser?.emailVerified]);
};
